import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useHiveHoney } from '../../context/HoneyFarmContext';
import HoneyCostPopup from './HoneyCostPopup';
import HoneyDContract from '../../helpers/HONEYDContract';
import { ethers } from 'ethers';
import fetchData from '../../helpers/fetchMetadata';
import rarities from '../../assets/Data/hive_rarities';
import config from '../../config';

// Honey costs for actions (in HONEYD)
const MINT_BEE_COST = 23;
const REACTIVATE_COST = 69;

// Filter types
type RarityFilter = 'all' | 0 | 1 | 2 | 3 | 4;
type StatusFilter = 'all' | 'active' | 'inactive';

const RARITY_LABELS: Record<number, string> = {
  0: 'Common',
  1: 'Uncommon',
  2: 'Rare',
  3: 'Epic',
  4: 'Legendary',
};

const RARITY_COLORS: Record<number, string> = {
  0: 'text-gray-400 border-gray-400',
  1: 'text-green-400 border-green-400',
  2: 'text-blue-400 border-blue-400',
  3: 'text-purple-400 border-purple-400',
  4: 'text-[#fcd34d] border-[#fcd34d]',
};

// Get rarity for a hive ID
const getHiveRarity = (hiveId: number): number => {
  const rarityEntry = rarities.find((r: any) => Number(r.id) === hiveId);
  return rarityEntry ? Number(rarityEntry.rarity) : 0;
};

interface HivesProps {
  signer?: ethers.Signer;
  address?: string;
}

interface MintedBeePopupProps {
  beeId: number;
  beeImage: string | null;
  onClose: () => void;
}

const MintedBeePopup: React.FC<MintedBeePopupProps> = ({ beeId, beeImage, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80">
      <div className="bg-black bg-opacity-90 border-2 border-[#c4f6f9] rounded-lg shadow-lg w-full max-w-md p-6 relative">
        <button
          className="absolute top-3 right-3 text-gray-400 hover:text-white text-2xl"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <h2 className="text-xl font-bold text-center text-[#c4f6f9] mb-4">
          BEE MINTED
        </h2>
        <div className="flex flex-col items-center">
          {beeImage ? (
            <img 
              src={beeImage} 
              alt={`Bee #${beeId}`} 
              className="w-48 h-48 rounded-lg object-cover mb-4 border-2 border-[#ffa2f8]"
            />
          ) : (
            <div className="w-48 h-48 rounded-lg bg-gray-800 flex items-center justify-center mb-4 border-2 border-[#ffa2f8]">
              <span className="text-[#c4f6f9] text-lg">Loading...</span>
            </div>
          )}
          <p className="text-[#55bac3] text-lg">Bee #{beeId}</p>
          <p className="text-gray-400 text-sm mt-2">Your new bee has been minted.</p>
        </div>
        <div className="flex justify-center mt-6">
          <button
            className="px-6 py-2 bg-transparent border-2 border-[#ffa2f8] text-[#ffa2f8] rounded hover:bg-[#ffa2f8] hover:text-black transition-colors text-sm"
            onClick={onClose}
          >
            CLOSE
          </button>
        </div>
      </div>
    </div>
  );
};

const Hives: React.FC<HivesProps> = ({ signer, address }) => {
  const { hives, loading, refreshStates, mintBeeFromHive, reactivateHive } = useHiveHoney();
  
  const [honeyBalance, setHoneyBalance] = useState<number>(0);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [mintedBee, setMintedBee] = useState<{ beeId: number; beeImage: string | null } | null>(null);
  const [rarityFilter, setRarityFilter] = useState<RarityFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchId, setSearchId] = useState<string>('');

  // Read provider - bypasses MetaMask for read operations
  const readProvider = useMemo(() => {
    return new ethers.providers.JsonRpcProvider(config.infuraUrl);
  }, []);

  // Read contract (uses direct RPC to bypass MetaMask rate limits)
  const honeyReadContract = useMemo(() => {
    return new HoneyDContract(readProvider);
  }, [readProvider]);

  // Load honey balance
  const loadHoneyBalance = useCallback(async () => {
    if (!honeyReadContract || !address) return;
    
    try {
      const balance = await honeyReadContract.balanceOf(address);
      // Convert from wei to ether (HONEYD has 18 decimals)
      const formattedBalance = parseFloat(ethers.utils.formatEther(balance));
      setHoneyBalance(formattedBalance);
    } catch (error) {
      console.error('Error loading honey balance:', error);
      setHoneyBalance(0);
    }
  }, [honeyReadContract, address]);

  // Load honey balance on mount and when dependencies change
  useEffect(() => {
    loadHoneyBalance();
  }, [loadHoneyBalance]);

  const handleMint = useCallback(async (hiveId: number) => {
    if (!mintBeeFromHive) return;
    setActionLoading(hiveId);
    let mintedBeeId: number | undefined;
    try {
      const result = await mintBeeFromHive(hiveId);
      
      // If we got a bee ID back, prepare to show the popup
      if (result?.beeId) {
        mintedBeeId = result.beeId;
      }
    } catch (err: any) {
      // User rejected or wallet error - don't log as error if user cancelled
      if (err?.code === 4001 || err?.code === 'ACTION_REJECTED') {
        console.log('User cancelled transaction');
      } else {
        console.error('Mint bee error:', err);
      }
    } finally {
      setActionLoading(null);
    }
    
    // Show minted bee popup and refresh in background after modal closes
    if (mintedBeeId) {
      // Fetch bee metadata for the image
      try {
        const metaRes = await fetchData(mintedBeeId, 'bees');
        const meta = await metaRes.json();
        setMintedBee({ beeId: mintedBeeId, beeImage: meta.image || null });
      } catch (err) {
        console.error('Error fetching bee metadata:', err);
        setMintedBee({ beeId: mintedBeeId, beeImage: null });
      }
    }
    refreshStates().catch(console.error);
    loadHoneyBalance().catch(console.error);
  }, [mintBeeFromHive, refreshStates, loadHoneyBalance]);

  const handleReactivate = useCallback(async (hiveId: number) => {
    if (!reactivateHive) return;
    setActionLoading(hiveId);
    try {
      await reactivateHive(hiveId);
    } catch (err: any) {
      // User rejected or wallet error - don't log as error if user cancelled
      if (err?.code === 4001 || err?.code === 'ACTION_REJECTED') {
        console.log('User cancelled transaction');
      } else {
        console.error('Reactivate hive error:', err);
      }
    } finally {
      setActionLoading(null);
    }
    // Refresh in background after modal closes
    refreshStates().catch(console.error);
    loadHoneyBalance().catch(console.error);
  }, [reactivateHive, refreshStates, loadHoneyBalance]);

  const canMintBee = (hive: any) => {
    // Can mint if usage < 3 and has enough honey
    return hive.usage < 3 && honeyBalance >= MINT_BEE_COST;
  };

  const canReactivate = (hive: any) => {
    // Can reactivate if usage >= 3 and has enough honey
    return hive.usage >= 3 && honeyBalance >= REACTIVATE_COST;
  };

  const needsReactivation = (hive: any) => {
    return hive.usage >= 3;
  };

  // Filter hives by rarity, status, and search ID
  const filteredHives = useMemo(() => {
    return hives.filter((hive: any) => {
      // Filter by search ID
      if (searchId && hive.id.toString() !== searchId) {
        return false;
      }
      
      // Filter by rarity
      if (rarityFilter !== 'all') {
        const hiveRarity = getHiveRarity(hive.id);
        if (hiveRarity !== rarityFilter) {
          return false;
        }
      }
      
      // Filter by status (active/inactive)
      if (statusFilter !== 'all') {
        const isInactive = hive.usage >= 3;
        if (statusFilter === 'active' && isInactive) {
          return false;
        }
        if (statusFilter === 'inactive' && !isInactive) {
          return false;
        }
      }
      
      return true;
    });
  }, [hives, rarityFilter, statusFilter, searchId]);

  // Get rarity counts for display
  const rarityCounts = useMemo(() => {
    const counts: Record<number, number> = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 };
    hives.forEach((hive: any) => {
      const rarity = getHiveRarity(hive.id);
      counts[rarity]++;
    });
    return counts;
  }, [hives]);

  // Get status counts for display
  const statusCounts = useMemo(() => {
    let active = 0;
    let inactive = 0;
    hives.forEach((hive: any) => {
      if (hive.usage >= 3) {
        inactive++;
      } else {
        active++;
      }
    });
    return { active, inactive };
  }, [hives]);

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Header */}
      <div className="w-full p-4 bg-black bg-opacity-60 text-white shadow-md rounded-md mb-6 border border-[#c4f6f9]">
        <h1 className="text-xl font-bold text-[#c4f6f9]">My Hives</h1>
        <div className="text-sm text-gray-300 mt-2">
          Hives can mint bees (limited to 3 per cycle). After minting 3 bees, reactivate your hive to mint more.
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-sm">
          <span className="text-gray-400">
            Balance: <span className="text-[#fcd34d]">{honeyBalance.toFixed(2)} $HONEYD</span>
          </span>
          <span className="text-gray-400">
            Mint Cost: <span className="text-[#c4f6f9]">{MINT_BEE_COST}</span>
          </span>
          <span className="text-gray-400">
            Reactivate: <span className="text-[#ffa2f8]">{REACTIVATE_COST}</span>
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="w-full p-4 bg-black bg-opacity-60 text-white shadow-md rounded-md mb-6 border border-[#55bac3]">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {/* Search by ID */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-400">Search:</label>
            <input
              type="text"
              placeholder="Hive ID"
              value={searchId}
              onChange={(e) => setSearchId(e.target.value.replace(/\D/g, ''))}
              className="w-24 px-3 py-1 bg-black border border-[#55bac3] rounded-md text-white text-sm focus:outline-none focus:border-[#c4f6f9]"
            />
            {searchId && (
              <button
                onClick={() => setSearchId('')}
                className="text-gray-400 hover:text-white text-sm"
              >
                ×
              </button>
            )}
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-400">Status:</label>
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1 rounded-md text-sm transition-colors ${
                statusFilter === 'all'
                  ? 'bg-[#55bac3] text-black'
                  : 'bg-transparent border border-[#55bac3] text-[#55bac3] hover:bg-[#55bac3]/20'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setStatusFilter('active')}
              className={`px-3 py-1 rounded-md text-sm transition-colors ${
                statusFilter === 'active'
                  ? 'bg-[#55bac3] text-black'
                  : 'bg-transparent border border-[#55bac3] text-[#55bac3] hover:bg-[#55bac3]/20'
              }`}
            >
              Active ({statusCounts.active})
            </button>
            <button
              onClick={() => setStatusFilter('inactive')}
              className={`px-3 py-1 rounded-md text-sm transition-colors ${
                statusFilter === 'inactive'
                  ? 'bg-[#ffa2f8] text-black'
                  : 'bg-transparent border border-[#ffa2f8] text-[#ffa2f8] hover:bg-[#ffa2f8]/20'
              }`}
            >
              Inactive ({statusCounts.inactive})
            </button>
          </div>
        </div>

        {/* Rarity Filter */}
        <div className="flex items-center gap-2 flex-wrap mt-3">
          <label className="text-sm text-gray-400">Rarity:</label>
          <button
            onClick={() => setRarityFilter('all')}
            className={`px-3 py-1 rounded-md text-sm transition-colors ${
              rarityFilter === 'all'
                ? 'bg-[#55bac3] text-black'
                : 'bg-transparent border border-[#55bac3] text-[#55bac3] hover:bg-[#55bac3]/20'
            }`}
          >
            All ({hives.length})
          </button>
          {[0, 1, 2, 3, 4].map((rarity) => (
            <button
              key={rarity}
              onClick={() => setRarityFilter(rarity as RarityFilter)}
              className={`px-3 py-1 rounded-md text-sm transition-colors ${
                rarityFilter === rarity
                  ? `bg-opacity-100 ${RARITY_COLORS[rarity].replace('text-', 'bg-').replace('border-', '')} text-black`
                  : `bg-transparent border ${RARITY_COLORS[rarity]} hover:bg-opacity-20`
              }`}
            >
              {RARITY_LABELS[rarity]} ({rarityCounts[rarity]})
            </button>
          ))}
        </div>
        
        {/* Results count */}
        <div className="mt-3 text-sm text-gray-400">
          Showing {filteredHives.length} of {hives.length} hives
        </div>
      </div>

      {/* Hives List */}
      {loading ? (
        <div className="p-8 text-center bg-black bg-opacity-60 rounded-md border border-gray-700">
          <div className="animate-pulse text-[#c4f6f9]">Loading hives...</div>
        </div>
      ) : hives.length === 0 ? (
        <div className="p-8 text-center bg-black bg-opacity-60 rounded-md border border-gray-700">
          <div className="text-gray-400">You don't own any hives.</div>
          <div className="text-sm text-gray-500 mt-2">Mint a hive from one of your bears to start minting bees.</div>
        </div>
      ) : filteredHives.length === 0 ? (
        <div className="p-8 text-center bg-black bg-opacity-60 rounded-md border border-gray-700">
          <div className="text-gray-400">No hives match your filters.</div>
          <button 
            onClick={() => { setRarityFilter('all'); setStatusFilter('all'); setSearchId(''); }}
            className="text-[#55bac3] text-sm mt-2 hover:underline"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredHives.map((h: any) => {
            const isLoading = actionLoading === h.id;
            const needsReactivate = needsReactivation(h);
            const canPerformAction = needsReactivate ? canReactivate(h) : canMintBee(h);
            const hiveRarity = getHiveRarity(h.id);
            
            return (
              <div 
                key={h.id} 
                className="flex flex-col sm:flex-row items-center justify-between p-4 bg-black bg-opacity-60 text-white rounded-md shadow-md border border-[#55bac3]"
              >
                <div className="flex items-center gap-4 mb-4 sm:mb-0">
                  <img 
                    src={h.image} 
                    alt={`Hive #${h.id}`} 
                    className="w-20 h-20 rounded-md object-cover border border-[#c4f6f9]" 
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-base text-[#c4f6f9]">Hive #{h.id}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium border ${RARITY_COLORS[hiveRarity]}`}>
                        {RARITY_LABELS[hiveRarity]}
                      </span>
                    </div>
                    <div className="text-sm text-gray-300">
                      Minted: <span className={h.usage >= 3 ? 'text-[#ffa2f8]' : 'text-[#55bac3]'}>{h.usage ?? 0} / 3</span>
                    </div>
                    {needsReactivate && (
                      <div className="text-xs text-[#ffa2f8] mt-1">
                        Needs reactivation
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  {isLoading ? (
                    <div className="px-4 py-2 text-[#c4f6f9] animate-pulse">
                      Processing...
                    </div>
                  ) : needsReactivate ? (
                    <HoneyCostPopup
                      requiredHoney={REACTIVATE_COST}
                      addressHoney={honeyBalance}
                      callback={() => handleReactivate(h.id)}
                      callBackArgs={[]}
                      trigger={
                        <button
                          disabled={!canPerformAction}
                          className={`px-4 py-2 rounded-md text-sm transition-colors ${
                            canPerformAction 
                              ? 'bg-transparent border-2 border-[#ffa2f8] text-[#ffa2f8] hover:bg-[#ffa2f8] hover:text-black' 
                              : 'bg-gray-700 text-gray-500 cursor-not-allowed border-2 border-gray-600'
                          }`}
                        >
                          REACTIVATE
                        </button>
                      }
                    />
                  ) : (
                    <HoneyCostPopup
                      requiredHoney={MINT_BEE_COST}
                      addressHoney={honeyBalance}
                      callback={() => handleMint(h.id)}
                      callBackArgs={[]}
                      trigger={
                        <button
                          disabled={!canPerformAction}
                          className={`px-4 py-2 rounded-md text-sm transition-colors ${
                            canPerformAction 
                              ? 'bg-transparent border-2 border-[#55bac3] text-[#55bac3] hover:bg-[#55bac3] hover:text-black' 
                              : 'bg-gray-700 text-gray-500 cursor-not-allowed border-2 border-gray-600'
                          }`}
                        >
                          MINT BEE
                        </button>
                      }
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Minted Bee Popup */}
      {mintedBee && (
        <MintedBeePopup
          beeId={mintedBee.beeId}
          beeImage={mintedBee.beeImage}
          onClose={() => setMintedBee(null)}
        />
      )}

      {/* Processing Overlay */}
      {actionLoading !== null && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center">
          <div className="bg-black bg-opacity-90 border-2 border-[#ffa2f8] p-8 rounded-lg text-white text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ffa2f8] mx-auto mb-4"></div>
            <div className="text-[#ffa2f8]">Processing transaction...</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Hives;
